// Auth + payments API extensions (frontend).
// All requests use axios with `withCredentials: true` so the HttpOnly
// `session_token` cookie is sent with every /api/* call.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const http = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export const authApi = {
  me:       ()           => http.get('/auth/me').then(r => r.data),
  optionalMe: ()         => http.get('/auth/optional').then(r => r.data),
  session:  (session_id) => http.post('/auth/session', { session_id }).then(r => r.data),
  logout:   ()           => http.post('/auth/logout').then(r => r.data),
  passwordLogin: (email, password) =>
    http.post('/auth/password-login', { email, password }).then(r => r.data),
  requestPasswordReset: (email) =>
    http.post('/auth/password-reset/request', { email }).then(r => r.data),
  setKeys:  (patch)      => http.put('/auth/me/api-keys', patch).then(r => r.data),
  // iter55g50 — Forcibly extend the session cookie. Called from the
  // SessionTimeoutWarning modal five minutes before expiry.
  extend:   ()           => http.post('/auth/extend').then(r => r.data),
  // Feb-2026 · i18n — persist the user's preferred UI language so it
  // follows them across devices. Accepts a 2-letter ISO 639-1 code.
  setLanguage: (code)    => http.put('/auth/me/language', { language: code }).then(r => r.data),
  changePassword: (current_password, new_password) =>
    http.post('/auth/change-password', { current_password, new_password }).then(r => r.data),
  changeEmail: (current_password, new_email) =>
    http.post('/auth/change-email', { current_password, new_email }).then(r => r.data),
  verifyLoginEmail: (token) => http.get('/auth/change-email/verify', { params: { token } }).then(r => r.data),
  // 2026-10 — admin-only: mints a session for the fixed "Admin
  // Troubleshooting" sandbox writer account. 401s silently for any
  // non-admin caller (no admin_session cookie) — that's expected and
  // the caller should just treat it as "not an admin, stay logged out".
  adminLoomBridge: () => http.post('/auth/admin-loom-bridge').then(r => r.data),
  // 2026-06 — one-click "Continue as David" writer sign-in. No email
  // needed, but David's admin password is still required.
  davidSuiteLogin: (password) =>
    http.post('/auth/david-suite-login', { password }).then(r => r.data),
};

// iter-43 — Demo sandbox endpoints (no auth required to start).
export const demoApi = {
  start:     ()         => http.post('/demo/start', {}).then(r => r.data),
  status:    ()         => http.get('/demo/status').then(r => r.data),
  // iter-45 — lock-event telemetry (best-effort, silent failure).
  lockEvent: (feature)  => http.post('/demo/lock-event', { feature }).catch(() => null),
  metrics:   ()         => http.get('/admin/demo/metrics').then(r => r.data),
};

export const paymentsApi = {
  packages:        ()              => http.get('/payments/packages').then(r => r.data),
  checkoutPackage: (packageId, originUrl) => http.post('/payments/checkout/package', { package_id: packageId, origin_url: originUrl }).then(r => r.data),
  checkoutCustom:  (amountUsd, originUrl) => http.post('/payments/checkout/custom',  { amount_usd: amountUsd, origin_url: originUrl }).then(r => r.data),
  // iter-17 — self-serve Copyscape pack checkout (credits copyscape_tokens).
  startCopyscapePack: (packId, originUrl) => http.post('/payments/checkout/copyscape-pack', { pack_id: packId, origin_url: originUrl }).then(r => r.data),
  // Aliases used by the paywall modal — kept as simple call sites.
  startCustom:     (amountUsd, originUrl) => http.post('/payments/checkout/custom',  { amount_usd: amountUsd, origin_url: originUrl }).then(r => r.data),
  status:          (sessionId)     => http.get(`/payments/checkout/status/${sessionId}`).then(r => r.data),
  history:         ()              => http.get('/payments/history').then(r => r.data),
  // 2026-02 · Narration service checkout — unlocks full chapter generation for a project.
  checkoutNarration: (projectId, originUrl) =>
    http.post('/payments/checkout/narration', { project_id: projectId, origin_url: originUrl }).then(r => r.data),
};

// iter-18a — Subscription plans + Stripe Customer Portal.
export const subscriptionsApi = {
  plans:    ()                          => http.get('/subscriptions/plans').then(r => r.data),
  checkout: (planId, cadence, originUrl, withEditorAddon = false, promoCode = '', billingMode = 'standard') =>
    http.post('/subscriptions/checkout',
              { plan_id: planId, cadence, origin_url: originUrl,
                with_editor_addon: !!withEditorAddon, promo_code: promoCode || '',
                billing_mode: billingMode || 'standard' }).then(r => r.data),
  portal:   (returnUrl)                  => http.post('/subscriptions/portal',
                                                { return_url: returnUrl }).then(r => r.data),
  me:       ()                           => http.get('/subscriptions/me').then(r => r.data),
  // 2026-10 — Founding Member launch coupon status (public, no auth).
  foundingPromo: () => http.get('/subscriptions/founding-promo').then(r => r.data),
  // iter55z-audit round 9 — A/B-bucketed price for the Premier editor
  // add-on. Returns { price_usd, variant_id, source }. When the test
  // is disabled server-side the source is 'control' and the price
  // matches DEFAULT_PLANS.premier.editor_pass_addon_price_usd.
  editorAddonPrice: () => http.get('/subscriptions/editor-addon-price').then(r => r.data),
};

// iter-21 — Account usage time series (powers My Account daily chart)
export const accountApi = {
  usage:        ()        => http.get('/account/usage').then(r => r.data),
  usageSeries:  (days=30) => http.get('/account/usage/series', { params: { days } }).then(r => r.data),
};

// ---------- SSE streaming helper ----------
// Uses fetch with ReadableStream so we can attach the cookie + POST a body
// (the native EventSource only supports GET without credentials, which is too limiting).
export async function streamSSE(path, body, handlers) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    handlers?.onError?.(new Error(`HTTP ${res.status}`));
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  // SSE spec (Server-Sent Events, WHATWG) requires frames separated by
  // TWO line terminators. Real servers ship either `\n\n` (most Python
  // SSE libs) or `\r\n\r\n` (sse-starlette on some deployments). Handle
  // both, plus `\r\r`, so any consumer of streamSSE is bulletproof.
  const findFrameEnd = (s) => {
    let best = -1;
    let width = 0;
    for (const [delim, w] of [['\r\n\r\n', 4], ['\n\n', 2], ['\r\r', 2]]) {
      const i = s.indexOf(delim);
      if (i !== -1 && (best === -1 || i < best)) { best = i; width = w; }
    }
    return { idx: best, width };
  };
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    while (true) {
      const { idx, width } = findFrameEnd(buf);
      if (idx === -1) break;
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + width);
      const ev = parseSSEFrame(raw);
      if (!ev) continue;
      handlers?.onEvent?.(ev);
    }
  }
  handlers?.onClose?.();
}

function parseSSEFrame(raw) {
  let event = 'message';
  const dataLines = [];
  // Line terminators can be \n, \r\n or \r; normalise before splitting.
  for (const line of raw.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  const dataStr = dataLines.join('\n');
  try { return { event, data: JSON.parse(dataStr) }; }
  catch { return { event, data: dataStr }; }
}
